// Phase 7b — Pure derivation of `kind` + `payload` from composer intent.
//
// Producer sites on desktop (use-message-send.ts), iOS (AppModel.swift), and
// the server (actions.sendMessage fallback) all funnel through this helper so
// the kind-selection rule is enforced in exactly one place.
//
// Phase 7b PR C: image/gif mime → kind='gif' (single attachment). webp stays
// as image (cross-platform asymmetry: iOS picker over-classifies all webp as
// animated; desktop has no detection). The `/giphy` producer also stamps
// kind='gif' + payload={ url, search_term } directly without going through
// the helper.

import type { MessageKind } from "./message-payload.js";

// Field names match the actual upload response types (desktop UploadedAttachment
// in apps/desktop/src/hooks/use-attachment-upload.ts and iOS UploadResponse in
// apps/native/ios/Ano/Core/Networking/APIClient.swift). Both already have these
// fields — no normalization at producer call sites.
//
// Production `file_category` values come from server/utils.ts:42 getFileCategory:
// "image" | "video" | "audio" | "document" | "other". Mapping inside this helper:
//   image    -> kind='image'
//   video    -> kind='video'
//   audio    -> kind='audio'
//   document -> kind='file'
//   other    -> kind='file'  (fallback)
export interface AttachmentLike {
  id: string;
  file_category: string;
  file_type?: string | null;
}

export interface DeriveKindArgs {
  content: string;
  attachments: ReadonlyArray<AttachmentLike>;
}

export interface DeriveKindResult {
  kind: MessageKind;
  payload: Record<string, unknown> | null;
}

const URL_ONLY_RE = /^https?:\/\/\S+$/;

function categoryToKind(category: string): MessageKind {
  switch (category) {
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    default:
      return "file";
  }
}

function itemRefs(
  attachments: ReadonlyArray<AttachmentLike>,
): Array<{ attachment_id: string }> {
  return attachments.map((a) => ({ attachment_id: a.id }));
}

export function deriveMessageKind(args: DeriveKindArgs): DeriveKindResult {
  const { content, attachments } = args;
  const trimmed = content.trim();

  if (attachments.length === 0) {
    if (trimmed.length > 0 && URL_ONLY_RE.test(trimmed)) {
      return { kind: "link_only", payload: { url: trimmed } };
    }
    return { kind: "text", payload: null };
  }

  if (attachments.length === 1) {
    if (attachments[0].file_type === "image/gif") {
      return { kind: "gif", payload: null };
    }
    return {
      kind: categoryToKind(attachments[0].file_category),
      payload: null,
    };
  }

  const categories = new Set(
    attachments.map((a) => categoryToKind(a.file_category)),
  );
  if (categories.size === 1) {
    const [only] = categories;
    return {
      kind: only,
      payload: { items: itemRefs(attachments) },
    };
  }

  return {
    kind: "media_group",
    payload: { items: itemRefs(attachments) },
  };
}
